import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Phone } from "lucide-react";
import { Input } from "@/components/ui/input";
import Logo from "@/components/site/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("Subscribed. Our next trade digest is on its way.");
      setEmail("");
    } catch (err) {
      toast.error("Could not subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer id="contact" data-testid="site-footer" className="bg-[#0f2018] text-white pt-20 pb-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 border-b border-white/10 pb-16">
          <div className="lg:col-span-4">
            <Logo size={48} variant="light" />
            <p className="mt-6 text-white/60 max-w-sm leading-relaxed">
              Aadiyogis EXIM Traders — sourcing, certifying and delivering premium
              agricultural commodities to buyers across five continents.
            </p>

            <form onSubmit={subscribe} className="mt-8 flex gap-2 max-w-md" data-testid="newsletter-form">
              <Input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your work email"
                data-testid="newsletter-email"
                className="rounded-none bg-white/5 border-white/20 text-white placeholder:text-white/40 focus-visible:ring-[#C5A059]"
              />
              <button
                type="submit"
                data-testid="newsletter-submit"
                disabled={loading}
                className="btn-gold !py-2 !px-5 whitespace-nowrap text-sm"
              >
                {loading ? "..." : "Subscribe"}
              </button>
            </form>
            <p className="mt-3 text-xs text-white/40">Monthly trade digest. Unsubscribe anytime.</p>
          </div>

          <div className="lg:col-span-2">
            <p className="overline text-[#C5A059]">Explore</p>
            <ul className="mt-5 space-y-3 text-white/70 text-sm">
              <li><a href="#about" className="hover:text-[#C5A059]">About</a></li>
              <li><a href="#products" className="hover:text-[#C5A059]">Products</a></li>
              <li><a href="#reach" className="hover:text-[#C5A059]">Global Reach</a></li>
              <li><a href="#process" className="hover:text-[#C5A059]">Process</a></li>
              <li><a href="#insights" className="hover:text-[#C5A059]">Insights</a></li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <p className="overline text-[#C5A059]">Head Office</p>
            <ul className="mt-5 space-y-4 text-white/70 text-sm">
              <li className="flex gap-3">
                <MapPin size={18} strokeWidth={1.25} className="text-[#C5A059] shrink-0 mt-0.5" />
                <span>Kavali, Nellore, Andhra Pradesh, India</span>
              </li>
              <li className="flex gap-3">
                <Phone size={18} strokeWidth={1.25} className="text-[#C5A059] shrink-0 mt-0.5" />
                <a href="tel:+918019531084" className="hover:text-[#C5A059]">+91 80195 31084</a>
              </li>
              <li className="flex gap-3">
                <Mail size={18} strokeWidth={1.25} className="text-[#C5A059] shrink-0 mt-0.5" />
                <a href="mailto:aadiyogis108@gmail.com" className="hover:text-[#C5A059]">aadiyogis108@gmail.com</a>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-3">
            <p className="overline text-[#C5A059]">Regional Desks</p>
            <ul className="mt-5 space-y-3 text-white/70 text-sm">
              <li>Nellore, India · +91 80195 31084</li>
              <li>Hyderabad, India · +91 80195 31084</li>
              <li className="pt-2"><span className="overline text-[#C5A059] block mb-1">WhatsApp</span>
                <a href="https://wa.me/918019531084" target="_blank" rel="noreferrer" className="hover:text-[#C5A059]">+91 80195 31084</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-xs text-white/40">
          <div>© {new Date().getFullYear()} Aadiyogis EXIM Traders. All rights reserved.</div>
          <div className="flex gap-6">
            <a href="#" className="hover:text-[#C5A059]">Privacy</a>
            <a href="#" className="hover:text-[#C5A059]">Terms</a>
            <a href="/admin" className="hover:text-[#C5A059]" data-testid="admin-link">Admin</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
